/**
 * CallStage - the in-app call surface.
 *
 * Replaces the 900x700 popup window as the place a call is displayed, for three reasons that
 * all come from the same fact: the popup is a different document.
 *
 *  1. Full screen. The engineer asked for the call to go full screen when they answer. A
 *     full-screen request is only granted to a document that has transient activation, and
 *     the click that answers happens in THIS document - the popup has none of its own. There
 *     is no ordering of the popup path that can produce a full-screen call.
 *
 *  2. Screen sharing. `getDisplayMedia` needs the same activation, and the call UI ran in the
 *     opener's realm while the click happened in the popup, so the picker was refused far
 *     more often than it was cancelled - see the comment this replaces in VideoCallContent's
 *     share handler. Sharing a console is the single most useful thing on a recovery bridge,
 *     so it does not get to be the flakiest button in the room.
 *
 *  3. The popup itself. It is opened before the call exists purely to beat the pop-up
 *     blocker, has to be closed by its opener because `window` inside it is not it, and when
 *     it is blocked there is no call UI anywhere and the answered call has nowhere to go.
 *     All of that goes away with the window.
 *
 * The stage is a fixed, full-viewport overlay, so it is maximised whether or not the
 * full-screen request was granted. Refusal costs the call the OS chrome and nothing else,
 * which is why it is reported as a quiet notice here rather than as a call failure.
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import VideoCallContent from './VideoCallContent';
import './VideoCall.css';

// The stage covers the app, so - like the ring - its geometry is inline rather than trusting
// a stylesheet to arrive. A call the engineer cannot see is a call they cannot end.
const STAGE_STYLE = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    // Above everything else in the app, deliberately. At 9998 the stage lost to the legacy
    // visualisation modals - RunbookModal.css stacks at 9999/10000/10001 with !important and
    // RichTextEditModal.css at 10000 - so a runbook left open when a call started rendered
    // ON TOP of the full-screen call, over its controls and its only way to hang up. That was
    // unreachable while the call lived in its own window; putting the call in this document
    // is what makes the app's own stacking order the call's problem.
    zIndex: 100000,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden'
};

function CallStage(props) {
    const { t } = useTranslation();

    return (
        <div
            className="call-stage"
            style={STAGE_STYLE}
            role="dialog"
            aria-modal="true"
            aria-label={props.callType === 'audio'
                ? t('aiChat.calls.audioCall')
                : t('aiChat.calls.videoCall')}
        >
            <VideoCallContent {...props} isStage={true} />
        </div>
    );
}

export default CallStage;
